import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Stack, Box, Typography } from "@mui/material";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  // Firestore 기반 함수들 (현재 사용)
  createFirestoreTask,
  deleteFirestoreTask,
  updateFirestoreTask,
  postThread,
} from "../utils/taskDataFetch";
import { uploadMultipleMedia } from "../utils/mediaUploader";
 
// getMyCollections import 제거 - useCollections 훅 사용
import { isTextOverLimit, MAX_TEXT_LENGTH } from "../utils/textColorUtils";
import { saveDraft, getDrafts, deleteDraft, loadDraftToEditor } from "../utils/draftManager";
import { useActiveTasksSubscription } from "../hooks/useTaskSubscription";
import { useUser } from "../contexts/UserContext";
import { useCollections } from "../contexts/ThreadsContext";
import { useWritingMode } from "../contexts/WritingModeContext";
import { MIN_SCHEDULE_MINUTES, getMinScheduleDateTime, isValidScheduleTime } from "../constants/scheduleConfig";
import { useTranslation } from "react-i18next";
// useIsMobile hook 제거 - MUI sx로 반응형 처리
import { PageContainer } from "../components/Container/PageContainer";
import ThreadComposer from "../components/PostThreads/ThreadComposer";
import ScheduleSection from "../components/PostThreads/ScheduleSection";
import { ButtonBasic } from "../components/button/ButtonBasic";
import FullScreenEditor from "../components/PostThreads/FullScreenEditor";
import NotificationSection from "../components/PostThreads/NotificationSection";
import ScheduledPostsList from "../components/PostThreads/ScheduledPostsList";
import CollectionSelector from "../components/PostThreads/CollectionSelector";
import { postThreadWithRepliesDirect } from "../utils/threadsPostDirect";
import DataLoader from "../components/DataLoader";

const PostThreads = () => {
  const { t } = useTranslation();
  const { profile } = useUser();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // 모드 판별
  const mode = useMemo(() => {
    if (location.pathname.includes('/scheduled/')) return 'edit-scheduled';
    if (location.pathname.includes('/draft/')) return 'edit-draft';
    return 'new';
  }, [location.pathname]);
  
  const isEditMode = mode !== 'new';

  // WritingMode Context 사용
  const { isFocusMode } = useWritingMode();

  // useIsMobile hook 제거 - MUI sx로 반응형 처리

  // 새로운 threads 기반 상태
  const [threads, setThreads] = useState([{ id: 1, text: "", isMain: true }]);
  // 미디어 파일 상태 (threadId 단위 관리)
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  // threadHelpers 삭제 - 더 이상 필요 없음

  // 기존 상태들 (호환성 유지)
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(""); // 로딩 단계 표시
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isScheduled, setIsScheduled] = useState(false); // 예약모드 디폴트 - false로 변경
  const [scheduleDateTime, setScheduleDateTime] = useState(() => {
    return getMinScheduleDateTime(); // 전역 설정 사용
  });

  // 컬렉션 관련 상태 - useCollections 훅 사용
  const { collections: myCollections, loadCollections } = useCollections();
  const [collectionConfig, setCollectionConfig] = useState({
    mode: "none",
    autoAddToCollection: false,
  });

  // 사용자 ID
  const userID = profile?.id || localStorage.getItem("userID");
  console.log('[PostThreads] User ID:', userID, 'profile:', profile);

  // 임시저장 관련 상태
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [drafts, setDrafts] = useState([]);
  // const [showDraftsList, setShowDraftsList] = useState(false);
  const [draftUpdateTrigger, setDraftUpdateTrigger] = useState(0); // 임시저장 완료 추적

  // 탭 상태 추가
  const [activeTab, setActiveTab] = useState('scheduled'); // 'scheduled' | 'drafts'

  // edit-draft 모드 변경 감지용 스냅샷
  const [baselineDraftSnapshot, setBaselineDraftSnapshot] = useState(null);

  // 중복 제출 가드용 ref
  const inFlightRef = useRef(false);
  const immediateResetDoneRef = useRef(false);
  const skipNextNewModeResetRef = useRef(false);

  // 간단 해시(djb2) 유틸 및 로컬 멱등 지문 생성기
  const djb2Hash = useCallback((str) => {
    let hash = 5381;
    for (let i = 0; i < str.length; i += 1) {
      hash = ((hash << 5) + hash) + str.charCodeAt(i);
    }
    return (hash >>> 0).toString(16);
  }, []);

  const computeRequestFingerprint = useCallback((params) => {
    const { threadsList, mediaList, when, scheduled } = params;
    const legacy = convertThreadsToLegacyFormat(threadsList);
    const safe = {
      text: (legacy.text || '').trim(),
      replies: (legacy.replies || []).map((t) => (t || '').trim()),
      media: (mediaList || []).map((m) => ({
        url: m.url || '',
        name: m.name || m.fileName || '',
        threadId: m.threadId != null ? m.threadId : null
      })),
      when: scheduled && when ? new Date(when).toISOString() : null,
      scheduled: !!scheduled
    };
    return djb2Hash(JSON.stringify(safe));
  }, [djb2Hash]);

  const mainThreadId = useMemo(() => {
    const mainThread = threads.find((t) => t.isMain);
    if (mainThread) return mainThread.id;
    return threads[0]?.id ?? 'main';
  }, [threads]);

  const mediaByThread = useMemo(() => {
    const map = new Map();
    threads.forEach((thread) => {
      map.set(thread.id, []);
    });

    const ensureBucket = (threadId) => {
      if (!map.has(threadId)) {
        map.set(threadId, []);
      }
      return map.get(threadId);
    };

    (mediaFiles || []).forEach((file) => {
      const ownerId = (file && file.threadId != null) ? file.threadId : mainThreadId;
      const bucket = ensureBucket(ownerId);
      bucket.push(file);
    });

    return map;
  }, [mediaFiles, threads, mainThreadId]);

  const handleThreadMediaChange = useCallback((threadId, files) => {
    const targetId = threadId != null ? threadId : mainThreadId;
    setMediaFiles((prev) => {
      const previous = Array.isArray(prev) ? prev : [];
      const others = previous.filter((item) => {
        const owner = (item && item.threadId != null) ? item.threadId : mainThreadId;
        return owner !== targetId;
      });

      const normalized = (files || []).map((item) => {
        if (!item) return item;
        if (item.threadId === targetId) return item;
        return { ...item, threadId: targetId };
      }).filter(Boolean);

      return [...others, ...normalized];
    });
  }, [mainThreadId]);

  const handleThreadsChange = useCallback((updatedThreads) => {
    setThreads(updatedThreads);
    setMediaFiles((prev) => {
      const previous = Array.isArray(prev) ? prev : [];
      const newMainId = updatedThreads.find((t) => t.isMain)?.id ?? (updatedThreads[0]?.id ?? 'main');
      const allowedIds = new Set(updatedThreads.map((t) => t.id));

      return previous
        .filter((item) => {
          const owner = (item && item.threadId != null) ? item.threadId : newMainId;
          return owner === newMainId || allowedIds.has(owner);
        })
        .map((item) => {
          if (!item.threadId) {
            return { ...item, threadId: newMainId };
          }
          return item;
        });
    });
  }, []);

  const inflightKey = 'mti_inflight_map';
  const inflightTtlMs = 10 * 60 * 1000; // 10분 TTL
  const successKey = 'mti_recent_success_map';
  const successTtlMs = 10 * 60 * 1000; // 10분 TTL

  const readInflightMap = useCallback(() => {
    try {
      const raw = localStorage.getItem(inflightKey);
      if (!raw) return {};
      const obj = JSON.parse(raw);
      const now = Date.now();
      // TTL 만료 제거
      Object.keys(obj).forEach((k) => { if ((now - (obj[k] || 0)) > inflightTtlMs) delete obj[k]; });
      return obj;
    } catch (_) { return {}; }
  }, [inflightTtlMs]);

  const writeInflightMap = useCallback((obj) => {
    try { localStorage.setItem(inflightKey, JSON.stringify(obj)); } catch (_) {}
  }, []);

  const readSuccessMap = useCallback(() => {
    try {
      const raw = localStorage.getItem(successKey);
      if (!raw) return {};
      const obj = JSON.parse(raw);
      const now = Date.now();
      Object.keys(obj).forEach((k) => { if ((now - (obj[k] || 0)) > successTtlMs) delete obj[k]; });
      return obj;
    } catch (_) { return {}; }
  }, [successTtlMs]);

  const writeSuccessMap = useCallback((obj) => {
    try { localStorage.setItem(successKey, JSON.stringify(obj)); } catch (_) {}
  }, []);

  const createNormalizedSnapshot = useCallback((src) => {
    if (!src) return null;
    const normalizeThreads = (threadsList) =>
      (threadsList || []).map((t) => ({ id: t.id, text: t.text || '', isMain: !!t.isMain }));

    const normalizeMedia = (mediaList) =>
      (mediaList || []).map((m) => ({
        url: m.url || '',
        name: m.name || m.fileName || '',
        type: m.type || m.fileType || '',
        size: m.size || m.fileSize || 0,
        threadId: m.threadId != null ? m.threadId : null,
      }));

    const normalizeCollectionConfig = (cfg) => ({
      mode: cfg?.mode || 'none',
      collectionId: cfg?.collectionId,
      newCollectionTitle: cfg?.newCollectionTitle || '',
      newCollectionDescription: cfg?.newCollectionDescription || '',
      autoAddToCollection: !!cfg?.autoAddToCollection,
    });

    const snapshot = {
      threads: normalizeThreads(src.threads),
      mediaFiles: normalizeMedia(src.mediaFiles),
      collectionConfig: normalizeCollectionConfig(src.collectionConfig),
      scheduleDateTime: src.scheduleDateTime ? new Date(src.scheduleDateTime).toISOString() : null,
    };
    return snapshot;
  }, []);

  const getCurrentStateSnapshot = useCallback(() => {
    return createNormalizedSnapshot({
      threads,
      mediaFiles,
      collectionConfig,
      scheduleDateTime,
    });
  }, [threads, mediaFiles, collectionConfig, scheduleDateTime, createNormalizedSnapshot]);

  const isDraftDirty = useMemo(() => {
    if (mode !== 'edit-draft' || !baselineDraftSnapshot) return false;
    try {
      const current = getCurrentStateSnapshot();
      return JSON.stringify(current) !== JSON.stringify(baselineDraftSnapshot);
    } catch (e) {
      return true; // 비교 실패 시 저장 가능하도록 처리
    }
  }, [mode, baselineDraftSnapshot, getCurrentStateSnapshot]);

  // Firestore 실시간 구독 (새로운 방식)
  const {
    tasks: firestoreTasks,
    loading: firestoreLoading,
    error: firestoreError,
  } = useActiveTasksSubscription(userID);

  // ThreadComposer 변경 핸들러 - 삭제 (더 이상 helpers 필요 없음)

  // 500자 초과 체크
  const hasOverLimitText = threads.some((thread) =>
    isTextOverLimit(thread.text, MAX_TEXT_LENGTH)
  );

  // 메인 스레드가 비어있는지 체크
  const mainThread = threads.find((t) => t.isMain);
  const hasNoContent = !(
    ((() => {
      const val = mainThread?.text;
      if (typeof val === 'string') return val;
      if (val === null || val === undefined) return '';
      if (typeof val === 'object') {
        try {
          const keys = Object.keys(val);
          if (keys.length > 0 && keys.every(k => !isNaN(parseInt(k, 10)))) {
            return keys
              .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
              .map(k => val[k])
              .join('');
          }
        } catch (_) {}
      }
      return String(val);
    })() || ''
  ).trim());
  
  // Draft 저장은 내용이 비어있어도 가능하도록 별도 체크
  const hasNoDraftContent = threads.every(t => {
    const val = t?.text;
    let str;
    if (typeof val === 'string') str = val;
    else if (val === null || val === undefined) str = '';
    else if (typeof val === 'object') {
      try {
        const keys = Object.keys(val);
        if (keys.length > 0 && keys.every(k => !isNaN(parseInt(k, 10)))) {
          str = keys.sort((a, b) => parseInt(a, 10) - parseInt(b, 10)).map(k => val[k]).join('');
        } else {
          str = String(val);
        }
      } catch (_) { str = String(val); }
    } else {
      str = String(val);
    }
    return !str.trim();
  }) && mediaFiles.length === 0;

  const isPostDisabled = loading || hasOverLimitText || hasNoContent;
  // 전역 액션 비활성(중복 제출 방지)
  const isActionDisabled = isPostDisabled || inFlightRef.current;

  // UI 텍스트 헬퍼 함수들
  // const getPageTitle = () => {
  //   switch(mode) {
  //     case 'edit-scheduled': return '예약글 수정';
  //     case 'edit-draft': return '임시저장글 수정';
  //     default: return '새 스레드 작성';
  //   }
  // };

  // 게시글-스레드 변환 헬퍼
  const convertPostToThreads = useCallback((post) => {
    const threadsResult = [];
    const mainThreadId = 1;
    threadsResult.push({ id: mainThreadId, text: post?.text || '', isMain: true });

    if (Array.isArray(post?.replies)) {
      post.replies.forEach((reply, index) => {
        const replyId = reply && typeof reply === 'object' && reply.id != null
          ? reply.id
          : (index + 2);
        let replyText = '';
        if (typeof reply === 'string') {
          replyText = reply;
        } else if (reply && typeof reply === 'object') {
          replyText = reply.text || '';
        }
        threadsResult.push({ id: replyId, text: replyText, isMain: false });
      });
    } else if (post?.reply) {
      const reply = post.reply;
      const replyId = (reply && typeof reply === 'object' && reply.id != null) ? reply.id : 2;
      const replyText = typeof reply === 'string' ? reply : (reply?.text || '');
      threadsResult.push({ id: replyId, text: replyText, isMain: false });
    }

    return threadsResult;
  }, []);

  const normalizeMediaForEditor = useCallback((mediaList, fallbackThreadId = null) => {
    if (!Array.isArray(mediaList)) return [];
    return mediaList.map((item, index) => {
      const url = item?.url || item?.preview || '';
      const name = item?.name || item?.fileName || `media-${index + 1}`;
      const type = item?.type || item?.fileType || '';
      const size = item?.size || item?.fileSize || 0;
      const threadId = item?.threadId != null ? item.threadId : fallbackThreadId;

      return {
        id: item?.id || item?.storagePath || url || `media-${index}`,
        url,
        preview: url,
        name,
        fileName: item?.fileName || name,
        type,
        fileType: type,
        size,
        fileSize: size,
        storagePath: item?.storagePath,
        isTemporary: !!item?.isTemporary,
        needsReupload: !!item?.needsReupload,
        threadId
      };
    });
  }, []);

  const buildMediaFilesFromPost = useCallback((post, threadsList) => {
    const results = [];
    if (!post || !Array.isArray(threadsList)) return results;

    const mainThread = threadsList.find((t) => t.isMain);
    if (mainThread) {
      results.push(...normalizeMediaForEditor(post.media || [], mainThread.id));
    }

    const replyThreads = threadsList.filter((t) => !t.isMain);
    if (Array.isArray(post.replies)) {
      post.replies.forEach((reply, index) => {
        const replyThreadId = (reply && typeof reply === 'object' && reply.id != null)
          ? reply.id
          : (replyThreads[index]?.id ?? (index + 2));
        const replyMedia = (reply && typeof reply === 'object' && Array.isArray(reply.media)) ? reply.media : [];
        if (replyThreadId != null) {
          results.push(...normalizeMediaForEditor(replyMedia, replyThreadId));
        }
      });
    } else if (post.reply && typeof post.reply === 'object') {
      const replyThreadId = post.reply.id != null ? post.reply.id : (replyThreads[0]?.id ?? 2);
      results.push(...normalizeMediaForEditor(post.reply.media || [], replyThreadId));
    }

    return results;
  }, [normalizeMediaForEditor]);

  const extractCollectionConfig = useCallback((post) => {
    if (post.collectionId) {
      return {
        mode: 'existing',
        collectionId: post.collectionId,
        autoAddToCollection: post.autoAddToCollection || false,
      };
    } else if (post.createNewCollection) {
      return {
        mode: 'new',
        newCollectionTitle: post.createNewCollection.title,
        newCollectionDescription: post.createNewCollection.description,
        autoAddToCollection: post.autoAddToCollection || false,
      };
    } else {
      return { mode: 'none', autoAddToCollection: post.autoAddToCollection || false };
    }
  }, []);

  // 초안 목록 불러오기 (useCallback로 안정화)
  const loadDrafts = useCallback(async () => {
    try {
      console.log('[PostThreads] Loading drafts for userID:', userID);
      const draftsList = await getDrafts(userID);
      console.log('[PostThreads] Loaded drafts:', draftsList);
      setDrafts(draftsList);
      // setShowDraftsList(true);
    } catch (error) {
      console.error('[PostThreads] Failed to load drafts:', error);
      setError(error.message);
    }
  }, [userID]);

  // 예약글 로딩 함수 (useCallback로 안정화)
  const loadScheduledPost = useCallback(async (postId) => {
    try {
      setLoading(true);
      const post = firestoreTasks.find((task) => task.id === postId);
      if (post) {
        const threadsFromPost = convertPostToThreads(post);
        setThreads(threadsFromPost);
        setMediaFiles(buildMediaFilesFromPost(post, threadsFromPost));
        // 예약 편집 진입 시 예약 토글/시간 반영
        setIsScheduled(true);
        try {
          let parsedDate = null;
          if (typeof post.executeAt === 'string') {
            const [dateStr, timeStr] = post.executeAt.split(' ');
            const [yy, mm, dd] = (dateStr || '').split('-').map(v => parseInt(v, 10));
            const [hh, mi, ss] = (timeStr || '00:00:00').split(':').map(v => parseInt(v, 10));
            parsedDate = new Date(
              isNaN(yy) ? 1970 : yy,
              isNaN(mm) ? 0 : (mm - 1),
              isNaN(dd) ? 1 : dd,
              isNaN(hh) ? 0 : hh,
              isNaN(mi) ? 0 : mi,
              isNaN(ss) ? 0 : ss
            );
          } else {
            parsedDate = new Date(post.executeAt);
          }
          if (!isNaN(parsedDate.getTime())) {
            setScheduleDateTime(parsedDate);
          }
        } catch (_) {
          // 파싱 실패 시 기본값 유지
        }
        setCollectionConfig(extractCollectionConfig(post));
      } else {
        setError(t('postThreads.errors.scheduledPostNotFound'));
      }
    } catch (error) {
      setError(t('postThreads.errors.scheduledPostLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [firestoreTasks, convertPostToThreads, extractCollectionConfig, buildMediaFilesFromPost]);

  // 임시저장글 로딩 함수 (useCallback로 안정화)
  const loadDraftForEdit = useCallback(async (draftId) => {
    try {
      setLoading(true);
      const draftData = await loadDraftToEditor(userID, draftId);
      const draftThreads = draftData.threads || [{ id: 1, text: '', isMain: true }];
      setThreads(draftThreads);

      const mainIdForDraft = draftThreads.find((t) => t.isMain)?.id ?? (draftThreads[0]?.id ?? 1);
      const mainMedia = normalizeMediaForEditor(draftData.mediaFiles || [], mainIdForDraft);
      const replyMediaMap = draftData.replyMediaFiles || {};
      const replyMediaAggregated = Object.keys(replyMediaMap).flatMap((replyId) => {
        const numericId = Number(replyId);
        return normalizeMediaForEditor(replyMediaMap[replyId], Number.isNaN(numericId) ? replyId : numericId);
      });
      const mediaFilesVal = [...mainMedia, ...replyMediaAggregated];
      setMediaFiles(mediaFilesVal);
      const savedDateTime = draftData.scheduleDateTime;
      if (savedDateTime) {
        try {
          const dateObj = typeof savedDateTime === 'string' ? new Date(savedDateTime) : savedDateTime;
          if (isNaN(dateObj.getTime())) {
            console.warn('Invalid scheduleDateTime from draft, using default:', savedDateTime);
            setScheduleDateTime(getMinScheduleDateTime());
          } else {
            setScheduleDateTime(dateObj);
          }
        } catch (error) {
          console.warn('Error parsing scheduleDateTime from draft:', error);
          setScheduleDateTime(getMinScheduleDateTime());
        }
      } else {
        setScheduleDateTime(getMinScheduleDateTime());
      }
      setCollectionConfig(draftData.collectionConfig || { mode: 'none', autoAddToCollection: false });

      setBaselineDraftSnapshot(
        createNormalizedSnapshot({
          threads: draftData.threads,
          mediaFiles: mediaFilesVal,
          collectionConfig: draftData.collectionConfig || { mode: 'none', autoAddToCollection: false },
          scheduleDateTime: savedDateTime || getMinScheduleDateTime(),
        })
      );
    } catch (error) {
      setError(t('postThreads.errors.draftLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [userID, createNormalizedSnapshot, normalizeMediaForEditor]);

  const getButtonText = () => {
    if (loading && loadingStep) return loadingStep;
    return isScheduled ? t('postThreads.buttons.publishScheduled') : t('postThreads.buttons.publishNow');
  };

  // 텍스트 정규화 유틸리티 및 데이터 변환 헬퍼 함수들
  const normalizeTextValue = (text) => {
    if (typeof text === 'string') return text;
    if (text === null || text === undefined) return '';
    if (typeof text === 'object') {
      try {
        const keys = Object.keys(text);
        if (keys.length > 0 && keys.every(k => !isNaN(parseInt(k, 10)))) {
          return keys
            .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
            .map(k => text[k])
            .join('');
        }
      } catch (_) {}
    }
    return String(text);
  };

  const convertThreadsToLegacyFormat = (threadsData) => {
    const main = threadsData.find((t) => t.isMain);
    const repliesOnly = threadsData.filter((t) => !t.isMain);
    const normalizedReplies = repliesOnly
      .map((t) => normalizeTextValue(t.text))
      .map((s) => (s == null ? '' : String(s)))
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    return {
      text: normalizeTextValue(main?.text || ''),
      replies: normalizedReplies,
    };
  };

  const convertThreadsToRichFormat = (threadsData) => {
    const main = threadsData.find((t) => t.isMain);
    const repliesOnly = threadsData.filter((t) => !t.isMain);

    return {
      mainText: normalizeTextValue(main?.text || ''),
      replies: repliesOnly.map((t) => ({
        id: t.id,
        text: normalizeTextValue(t.text || '')
      }))
    };
  };

  // 🔶 Optimistic UI 상태: 예약/초안
  const [optimisticScheduledPosts, setOptimisticScheduledPosts] = useState([]);
  const [optimisticDrafts, setOptimisticDrafts] = useState([]);

  // 🔧 예약 항목 매칭 함수 (텍스트+시간 기준)
  const isSameScheduled = useCallback((a, b) => {
    const at = (a.text || '').trim();
    const bt = (b.text || '').trim();
    const ae = typeof a.executeAt === 'string' ? a.executeAt : (
      a.executeAt instanceof Date ? `${a.executeAt.getFullYear()}-${String(a.executeAt.getMonth()+1).padStart(2,'0')}-${String(a.executeAt.getDate()).padStart(2,'0')} ${String(a.executeAt.getHours()).padStart(2,'0')}:${String(a.executeAt.getMinutes()).padStart(2,'0')}:00` : ''
    );
    const be = typeof b.executeAt === 'string' ? b.executeAt : (
      b.executeAt instanceof Date ? `${b.executeAt.getFullYear()}-${String(b.executeAt.getMonth()+1).padStart(2,'0')}-${String(b.executeAt.getDate()).padStart(2,'0')} ${String(b.executeAt.getHours()).padStart(2,'0')}:${String(b.executeAt.getMinutes()).padStart(2,'0')}:00` : ''
    );
    return at === bt && ae === be;
  }, []);

  // 🔗 목록 결합: 예약
  const scheduledForUI = useMemo(() => {
    const base = Array.isArray(firestoreTasks) ? firestoreTasks : [];
    // 실행중(executing) 태스크는 카드에서 제외
    const baseNoExecuting = base.filter(item => (item && item.status) ? item.status !== 'executing' : true);
    const optimistic = Array.isArray(optimisticScheduledPosts) ? optimisticScheduledPosts : [];
    const filteredBase = baseNoExecuting.filter(item => !optimistic.some(opt => isSameScheduled(opt, item)));
    return [...optimistic, ...filteredBase];
  }, [firestoreTasks, optimisticScheduledPosts, isSameScheduled]);

  // 실행중(executing) 상태 존재 여부 → DataLoader 표시용
  const hasExecutingTasks = useMemo(() => {
    if (!Array.isArray(firestoreTasks)) return false;
    return firestoreTasks.some(t => t && t.status === 'executing');
  }, [firestoreTasks]);

  // 🔗 목록 결합: 초안 (낙관적 아이템은 저장 성공/실패 시 제거하므로 단순 결합)
  const draftsForUI = useMemo(() => {
    const optimistic = Array.isArray(optimisticDrafts) ? optimisticDrafts : [];
    return [...optimistic, ...drafts];
  }, [optimisticDrafts, drafts]);

  // 🔁 실데이터 도착 시 예약 낙관적 항목 정리
  useEffect(() => {
    if (!Array.isArray(firestoreTasks) || firestoreTasks.length === 0) return;
    if (optimisticScheduledPosts.length === 0) return;
    setOptimisticScheduledPosts(prev => prev.filter(opt => !firestoreTasks.some(real => isSameScheduled(opt, real))));
  }, [firestoreTasks, isSameScheduled, optimisticScheduledPosts.length]);

  // 컬렉션 데이터 로드
  const loadInitialData = useCallback(() => {
    const userIDVal = profile?.id || localStorage.getItem('userID');
    if (userIDVal) {
      loadCollections(userIDVal);
      // 초안도 미리 로드
      loadDrafts();
    }
  }, [profile?.id, loadCollections, loadDrafts]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Firestore 에러 처리
  useEffect(() => {
    if (firestoreError) {
      console.error("Firestore 실시간 구독 오류:", firestoreError);
      setError(t('postThreads.errors.firestoreSubscriptionError', { error: firestoreError }));
    }
  }, [firestoreError, t]);

  // 탭이 drafts로 변경되거나 임시저장 완료 시 초안 목록 로드
  const handleDraftsTabEffect = useCallback(() => {
    console.log('[PostThreads] Drafts update trigger:', { activeTab, userID, draftUpdateTrigger });
    if (activeTab === 'drafts' && userID) {
      console.log('[PostThreads] Loading drafts due to tab change or draft update');
      loadDrafts();
    }
  }, [activeTab, userID, draftUpdateTrigger, loadDrafts]);

  useEffect(() => {
    handleDraftsTabEffect();
  }, [handleDraftsTabEffect]);

  // 에디터 데이터 초기화 함수
  const resetEditorData = () => {
    setThreads([{ id: 1, text: "", isMain: true }]);
    setMediaFiles([]);
    setUploadProgress({});
    setScheduleDateTime(getMinScheduleDateTime());
    setCollectionConfig({ mode: "none", autoAddToCollection: false });
    setCurrentDraftId(null);
    setError(null);
    setSuccess(null);
  };

  // 모드 변경 시 데이터 로딩/리셋
  const loadByMode = useCallback(() => {
    if (isEditMode && id) {
      if (mode === 'edit-scheduled') {
        loadScheduledPost(id);
      } else if (mode === 'edit-draft') {
        loadDraftForEdit(id);
      }
    } else if (mode === 'new') {
      // 새글 작성 모드로 전환 시 데이터 초기화 (단, 직전 네비게이션으로 인한 중복 초기화는 건너뜀)
      if (skipNextNewModeResetRef.current) {
        skipNextNewModeResetRef.current = false;
      } else {
        resetEditorData();
      }
    }
  }, [isEditMode, id, mode, loadScheduledPost, loadDraftForEdit]);

  useEffect(() => {
    loadByMode();
  }, [loadByMode]);

  // 중복 정의 제거됨 (useCallback 버전 상단 정의 사용)

  // 중복 정의 제거됨 (useCallback 버전 상단 정의 사용)

  // 수정 모드 처리 함수
  const handleEditPost = async () => {
    try {
      // 공백/개행-only 댓글 사전 검증 (정규화 이전 검증)
      const emptyReplyIndexFromThreads = (threadsList) => {
        const repliesOnly = threadsList.filter((t) => !t.isMain);
        for (let i = 0; i < repliesOnly.length; i += 1) {
          const replyThread = repliesOnly[i];
          const txt = normalizeTextValue(replyThread.text || '').trim();
          const mediaBucket = mediaByThread instanceof Map
            ? (mediaByThread.get(replyThread.id) || [])
            : ((mediaByThread && mediaByThread[replyThread.id]) || []);
          const hasMedia = Array.isArray(mediaBucket) && mediaBucket.length > 0;
          if (txt.length === 0 && !hasMedia) return i; // 0-based reply index
        }
        return -1;
      };

      const invalidIdx = emptyReplyIndexFromThreads(threads);
      if (invalidIdx !== -1) {
        throw new Error(t('postThreads.errors.emptyReply', { index: invalidIdx + 1 }));
      }

      const {
        text,
        repliesForApi,
        mainMediaForApi,
        mainMediaFirestore,
        combinedProcessedMedia,
        replyMediaFirestore
      } = await preparePostData(`threads/${userID}/temp`);

      if (!text.trim()) {
        throw new Error(t("postThreads.errors.contentRequired"));
      }

      const invalidReplyIndex = repliesForApi.findIndex((reply) => {
        const textValue = typeof reply?.text === 'string' ? reply.text.trim() : '';
        const mediaCount = Array.isArray(reply?.media) ? reply.media.length : 0;
        return textValue.length === 0 && mediaCount === 0;
      });
      if (invalidReplyIndex !== -1) {
        throw new Error(t('postThreads.errors.emptyReplyGeneral', { index: invalidReplyIndex + 1 }));
      }

      if (mode === 'edit-scheduled') {
        // 예약 임박 제한: 예약 시간 2분 전부터 본문/댓글 수정 금지 (시간 변경은 별도 UI에서 수행)
        const now = new Date();
        const msUntil = scheduleDateTime.getTime() - now.getTime();
        const TWO_MIN_MS = 2 * 60 * 1000;
        if (msUntil <= TWO_MIN_MS) {
          throw new Error(t('postThreads.errors.editTimeRestriction'));
        }
        // 예약글 수정
        const year = scheduleDateTime.getFullYear();
        const month = String(scheduleDateTime.getMonth() + 1).padStart(2, "0");
        const day = String(scheduleDateTime.getDate()).padStart(2, "0");
        const hours = String(scheduleDateTime.getHours()).padStart(2, "0");
        const minutes = String(scheduleDateTime.getMinutes()).padStart(2, "0");
        const seconds = "00";
        const executeAt = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

        const updateData = {
          text,
          executeAt,
          replies: repliesForApi,
          autoAddToCollection: collectionConfig.autoAddToCollection || false,
          media: mainMediaForApi,
        };

        if (collectionConfig.mode === "existing" && collectionConfig.collectionId) {
          updateData.collectionId = collectionConfig.collectionId;
        } else if (collectionConfig.mode === "new" && collectionConfig.newCollectionTitle) {
          updateData.createNewCollection = {
            title: collectionConfig.newCollectionTitle,
            description: collectionConfig.newCollectionDescription || "",
          };
        }

        await updateFirestoreTask(id, updateData);
        setMediaFiles(combinedProcessedMedia);
        setSuccess(t('postThreads.actions.success.scheduledPostUpdated'));
        navigate('/post-thread');
      } else if (mode === 'edit-draft') {
        // 임시저장글 수정
        const draftData = {
          threads,
          mediaFiles: mainMediaFirestore || [],
          replyMediaFiles: replyMediaFirestore || {},
          collectionConfig,
          scheduleDateTime
        };

        const updated = await saveDraft(userID, draftData, id);
        setMediaFiles(combinedProcessedMedia);
        setSuccess(t('postThreads.actions.success.draftUpdated'));
        // 업데이트 후에도 동일 draft id를 유지하며 이어쓰기
        if (id) {
          navigate(`/post-thread/draft/${id}`, { replace: true });
        }
      }
    } catch (error) {
      throw error;
    }
  };

  // Threads API 직접 게시 함수 (2단계 프로세스) - pureDataFetch.js 패턴 활용
  const handleThreadsAPIPost = async () => {
    // 동기 중복 진입 가드
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    // 동일 세션 중 중복 리셋 방지 플래그 초기화
    immediateResetDoneRef.current = false;
    setLoading(true);
    setLoadingStep(t("postThreads.actions.loading.preparing"));
    setError(null);
    setSuccess(null);
    try {
      // 데이터 준비(업로드 포함)
      const {
        text,
        repliesForApi,
        mainMediaForApi,
        combinedProcessedMedia
      } = await preparePostData(`threads/${userID}/temp`);
      setMediaFiles(combinedProcessedMedia);

      // 🔑 즉시 게시용 taskId 생성 (멱등 지문 + timestamp)
      const reqFp = computeRequestFingerprint({
        threadsList: threads,
        mediaList: combinedProcessedMedia,
        when: new Date(),
        scheduled: false
      });
      const createdTaskId = `im-${reqFp}-${Date.now().toString(36)}`;

      // 즉시 게시: 예약 생성 경로 제거. 서버가 active_tasks를 'executing'으로 사전 생성.
      setLoadingStep(t('postThreads.actions.loading.schedulingPost'));
      // fire-and-forget (응답을 기다리지 않음)
      postThread(text, repliesForApi, mainMediaForApi, createdTaskId)
        .catch(err => {
          console.warn('[handleThreadsAPIPost] background postThread failed:', err?.message || err);
        });

      // 예약 탭으로 전환하여 카드에서 진행 상황 표시
      setActiveTab('scheduled');

      // 하단 카드 등장(활성화)까지 짧게 대기: active_tasks/{createdTaskId} 존재 확인
      try {
        const { dbService } = await import('../firebaseInstance');
        const activeRef = dbService
          .collection('user')
          .doc(userID)
          .collection('active_tasks')
          .doc(createdTaskId);
        const maxWaitMs = 5000;
        const intervalMs = 250;
        const start = Date.now();
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const snap = await activeRef.get();
          if (snap.exists) break;
          if (Date.now() - start >= maxWaitMs) break;
          await new Promise(r => setTimeout(r, intervalMs));
        }
      } catch (_) {}

      // 성공 처리 및 에디터 초기화 (버튼 로딩 종료, 하단 카드로 진행 상황 표시)
      if (!immediateResetDoneRef.current) {
        setSuccess(t('postThreads.actions.success.threadScheduled'));
        setThreads([{ id: 1, text: '', isMain: true }]);
        setMediaFiles([]);
        setUploadProgress({});
        setScheduleDateTime(getMinScheduleDateTime());
        setCollectionConfig({ mode: 'none', autoAddToCollection: false });
        setCurrentDraftId(null);
        immediateResetDoneRef.current = true;
      }

      // 임시저장에서 즉시 게시로 전환한 경우 임시저장글 삭제
      if (mode === 'edit-draft' && id) {
        try {
          await deleteDraft(userID, id);
          setDraftUpdateTrigger(prev => prev + 1);
        } catch (deleteError) {
          console.warn("임시저장글 삭제 중 오류:", deleteError);
        }
        // 드래프트 편집 포커스 해제 및 라우터 초기화
        skipNextNewModeResetRef.current = true;
        navigate('/post-thread', { replace: true });
      }

      // 예약글 편집 화면에서 즉시 게시한 경우, 원본 예약글 삭제
      if (mode === 'edit-scheduled' && id) {
        try {
          await deleteFirestoreTask(id);
        } catch (deleteError) {
          console.warn('예약글 삭제 중 오류:', deleteError);
        }
      }
      
    } catch (err) {
      console.error("즉시 게시 실패:", err);
      
      if (err.response) {
        const statusCode = err.response.status;
        if (statusCode === 401 || statusCode === 403) {
          setError(t("postThreads.errors.threadsApiUnauthorized"));
        } else if (statusCode === 429) {
          setError(t("postThreads.errors.threadsApiRateLimit"));
        } else if (statusCode >= 500) {
          setError(t("postThreads.errors.threadsApiServerError"));
        } else {
          setError(
            t("postThreads.errors.threadsApiGenericError", { 
              statusCode, 
              message: err.response.data?.message || err.message 
            })
          );
        }
      } else if (err.request) {
        setError(t("postThreads.errors.threadsApiNoResponse"));
      } else {
        setError(t("postThreads.errors.threadsApiConnectionFailed", { message: err.message }));
      }
    } finally {
      setLoading(false);
      setLoadingStep("");
      inFlightRef.current = false;
    }
  };

  // 프론트에서 Threads API로 직접 체이닝 게시 (텍스트 전용)
  const handleThreadsAPIPostDirect = async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    setLoadingStep(t('postThreads.actions.loading.publishing'));
    setError(null);
    setSuccess(null);
    try {
      // 미디어가 포함된 경우 직접 게시 비활성
      if (Array.isArray(mediaFiles) && mediaFiles.length > 0) {
        throw new Error(t('postThreads.errors.mediaNotSupportedForDirect') || '직접 게시에서는 미디어를 지원하지 않습니다.');
      }

      const { text, replies } = convertThreadsToLegacyFormat(threads);
      const safeReplies = (replies || []).map(r => (r == null ? '' : String(r).trim())).filter(Boolean);

      await postThreadWithRepliesDirect(text, safeReplies);

      // 임시저장/예약 편집 화면에서 즉시 게시한 경우 원본 삭제
      if (mode === 'edit-draft' && id) {
        try {
          await deleteDraft(userID, id);
          setDraftUpdateTrigger(prev => prev + 1);
        } catch (deleteError) {
          console.warn('임시저장글 삭제 중 오류:', deleteError);
        }
      }
      if (mode === 'edit-scheduled' && id) {
        try {
          await deleteFirestoreTask(id);
        } catch (deleteError) {
          console.warn('예약글 삭제 중 오류:', deleteError);
        }
      }

      setSuccess(t('postThreads.actions.success.threadPublished'));
      // 에디터 초기화 및 URL 초기화
      setThreads([{ id: 1, text: '', isMain: true }]);
      setMediaFiles([]);
      setUploadProgress({});
      setScheduleDateTime(getMinScheduleDateTime());
      setCollectionConfig({ mode: 'none', autoAddToCollection: false });
      setCurrentDraftId(null);
      navigate('/post-thread', { replace: true });
    } catch (err) {
      setError(err?.message || t('postThreads.errors.threadPublishFailed'));
    } finally {
      setLoading(false);
      setLoadingStep('');
      inFlightRef.current = false;
    }
  };

  // 삭제 함수
  const handleDelete = async () => {
    const confirmMessage = mode === 'edit-scheduled' 
      ? t('postThreads.confirm.deleteScheduledPost')
      : t('postThreads.confirm.deleteTempPost');
      
    if (!window.confirm(confirmMessage)) return;
    
    try {
      setLoading(true);
      if (mode === 'edit-scheduled') {
        await deleteFirestoreTask(id);
        setSuccess(t('postThreads.actions.success.scheduledPostDeleted'));
      } else if (mode === 'edit-draft') {
        await deleteDraft(userID, id);
        setSuccess(t('postThreads.actions.success.draftDeletedSuccess'));
        
        // 🔥 임시저장 삭제 완료 트리거 (drafts 리스트 업데이트)
        setDraftUpdateTrigger(prev => prev + 1);
      }
      navigate('/post-thread');
    } catch (error) {
      setError(t('postThreads.errors.deleteFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async () => {
    // 동기 중복 진입 가드
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    setLoadingStep(isEditMode ? t("postThreads.actions.loading.editing") : t("postThreads.actions.loading.preparing"));
    setError(null);
    setSuccess(null);
    console.log("handlePost 호출, 모드:", mode);
    
    try {
      // 공백/개행-only 댓글 사전 검증 (정규화 이전 검증)
      const emptyReplyIndexFromThreads = (threadsList) => {
        const repliesOnly = threadsList.filter((t) => !t.isMain);
        for (let i = 0; i < repliesOnly.length; i += 1) {
          const replyThread = repliesOnly[i];
          const txt = normalizeTextValue(replyThread.text || '').trim();
          const mediaBucket = mediaByThread instanceof Map
            ? (mediaByThread.get(replyThread.id) || [])
            : ((mediaByThread && mediaByThread[replyThread.id]) || []);
          const hasMedia = Array.isArray(mediaBucket) && mediaBucket.length > 0;
          if (txt.length === 0 && !hasMedia) return i; // 0-based reply index
        }
        return -1;
      };
      const invalidIdx = emptyReplyIndexFromThreads(threads);
      if (invalidIdx !== -1) {
        throw new Error(t('postThreads.errors.emptyReply', { index: invalidIdx + 1 }));
      }
      // 로컬 멱등 지문 생성 및 in-flight 중복 가드(10분 TTL)
      const requestFingerprint = computeRequestFingerprint({ threadsList: threads, mediaList: mediaFiles, when: scheduleDateTime, scheduled: isScheduled });
      // 최근 성공 이력 확인(사용자 재클릭에 의한 중복 요청 예방)
      const successMap = readSuccessMap();
      if (successMap[requestFingerprint]) {
        setSuccess(t('postThreads.actions.success.duplicateRequestPrevented'));
        // 성공 후 처리와 동일하게 UI 초기화만 수행
        setThreads([{ id: 1, text: '', isMain: true }]);
        setMediaFiles([]);
        setUploadProgress({});
        setScheduleDateTime(getMinScheduleDateTime());
        setCollectionConfig({ mode: 'none', autoAddToCollection: false });
        setCurrentDraftId(null);
        // 🔥 중복 방지 처리에서도 URL 초기화 제거, 예약 탭 유지
        if (isScheduled) setActiveTab('scheduled');
        return;
      }
      const inflight = readInflightMap();
      if (inflight[requestFingerprint]) {
        throw new Error(t('postThreads.errors.requestInProgress'));
      }
      inflight[requestFingerprint] = Date.now();
      writeInflightMap(inflight);

      // 수정 모드인 경우 별도 처리
      if (isEditMode) {
        // 🔥 임시저장글에서 예약게시 버튼을 눌렀다면 예약게시 생성 + 임시저장글 삭제
        if (mode === 'edit-draft' && isScheduled) {
          console.log("임시저장글을 예약게시로 전환 중...");
          // 임시저장글 삭제는 예약게시 성공 후에 처리
        } else {
          await handleEditPost();
          // 처리 완료시 in-flight 지문 제거
          const m = readInflightMap();
          delete m[requestFingerprint];
          writeInflightMap(m);
          return;
        }
      }
      // 🔥 예약 모드인 경우 버튼 클릭 시점에 3분 검증
      if (isScheduled) {
        if (!scheduleDateTime) {
          throw new Error(t("postThreads.errors.dateTimeRequired"));
        }

        if (!isValidScheduleTime(scheduleDateTime)) {
          throw new Error(t("postThreads.errors.minScheduleTime", { minutes: MIN_SCHEDULE_MINUTES }));
        }
        
        // 예약 임박(T-3분) + 새 업로드 존재 시 자동 연기 권고
        const now = new Date();
        const THREE_MIN_MS = 3 * 60 * 1000;
        const msUntil = scheduleDateTime.getTime() - now.getTime();
        const hasNewUploads = mediaFiles.some((m) => m && m.file instanceof File);
        if (msUntil <= THREE_MIN_MS && hasNewUploads) {
          const ok = window.confirm(t('postThreads.confirm.scheduleDelayConfirm'));
          if (ok) {
            const newDate = new Date(scheduleDateTime.getTime() + THREE_MIN_MS);
            setScheduleDateTime(newDate);
          }
        }

        console.log("✅ 3분 검증 통과 - 업로드 진행");
      }
      // 🔥 공통 데이터 준비 함수 사용 (예약게시용 경로 지정)
      const uploadPath = `threads/${userID}/temp`;
      const {
        text,
        repliesForApi,
        mainMediaForApi,
        combinedProcessedMedia,
        mainMediaFirestore,
        replyMediaFirestore
      } = await preparePostData(uploadPath);
      
      console.log("[handlePost] preparePostData 결과:", { 
        text: text?.substring(0, 50) + "...", 
        repliesCount: repliesForApi?.length, 
        mainMediaCount: mainMediaForApi?.length,
        combinedMediaCount: combinedProcessedMedia?.length
      });

      console.log("게시 모드 체크:", { isScheduled, mainMediaLength: mainMediaForApi.length });
      setMediaFiles(combinedProcessedMedia);
      const fingerprintMediaSnapshot = combinedProcessedMedia;
      const fingerprintThreadsSnapshot = threads;
      
      if (isScheduled) {
        // 🔥 최종 API 호출 직전에 예약 시간이 이미 지났는지만 확인
        const now = new Date();
        if (scheduleDateTime.getTime() <= now.getTime()) {
          throw new Error(t("postThreads.errors.scheduleTimeExpired"));
        }

        setLoadingStep(t("postThreads.actions.loading.schedulingPost"));

        // 날짜와 시간 조합하여 KST 형식으로 변환
        const year = scheduleDateTime.getFullYear();
        const month = String(scheduleDateTime.getMonth() + 1).padStart(2, "0");
        const day = String(scheduleDateTime.getDate()).padStart(2, "0");
        const hours = String(scheduleDateTime.getHours()).padStart(2, "0");
        const minutes = String(scheduleDateTime.getMinutes()).padStart(2, "0");
        const seconds = "00";

        const executeAt = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

        let response;

        // Firestore 기반 예약 생성
        console.log("Creating Firestore task...", { collectionConfig });
        const taskData = {
          text,
          replies: repliesForApi,
          executeAt: executeAt,
          autoAddToCollection: collectionConfig.autoAddToCollection || false,
        };

        // 미디어 정보 추가
        if (mainMediaForApi && mainMediaForApi.length > 0) {
          taskData.media = mainMediaForApi;
          console.log("[handlePost] 메인 미디어 데이터:", mainMediaForApi);
        }

        // 컬렉션 설정 추가
        if (
          collectionConfig.mode === "existing" &&
          collectionConfig.collectionId
        ) {
          taskData.collectionId = collectionConfig.collectionId;
        } else if (
          collectionConfig.mode === "new" &&
          collectionConfig.newCollectionTitle
        ) {
          // 새 컬렉션 생성 정보 추가
          taskData.createNewCollection = {
            title: collectionConfig.newCollectionTitle,
            description: collectionConfig.newCollectionDescription || "",
          };
        }

        // taskData에서 undefined 값 제거
        const cleanTaskData = {};
        Object.keys(taskData).forEach(key => {
          if (taskData[key] !== undefined && taskData[key] !== null) {
            cleanTaskData[key] = taskData[key];
          }
        });

        console.log("[handlePost] 정제된 taskData:", cleanTaskData);

        // ✅ Optimistic 예약 카드 추가
        const tempScheduled = {
          id: `temp-${Date.now()}`,
          text,
          executeAt: executeAt,
          replies: repliesForApi,
          media: cleanTaskData.media || [],
          userID: userID,
          autoAddToCollection: cleanTaskData.autoAddToCollection,
          collectionId: cleanTaskData.collectionId,
          createNewCollection: cleanTaskData.createNewCollection,
          status: 'scheduled',
          isTemporary: true,
          scheduledAt: Date.now(),
          createdAt: Date.now()
        };
        setOptimisticScheduledPosts(prev => [tempScheduled, ...prev]);
        // 예약 탭으로 즉시 전환
        setActiveTab('scheduled');

        try {
          response = await createFirestoreTask(cleanTaskData);
          console.log("Firestore task response", response);
        } catch (err) {
          // 실패 시 낙관적 카드 제거
          setOptimisticScheduledPosts(prev => prev.filter(p => p.id !== tempScheduled.id));
          throw err;
        }
        // Firestore는 실시간 구독으로 자동 업데이트됨

        // 🔥 임시저장글에서 예약게시로 전환한 경우 임시저장글 삭제
        if (mode === 'edit-draft' && id) {
          console.log("예약게시 성공 - 임시저장글 삭제 중...");
          try {
            await deleteDraft(userID, id);
            console.log("임시저장글 삭제 완료");
            
            // 🔥 임시저장 삭제 완료 트리거
            setDraftUpdateTrigger(prev => prev + 1);
          } catch (deleteError) {
            console.warn("임시저장글 삭제 중 오류:", deleteError);
            // 예약게시는 성공했으므로 삭제 실패는 경고만 표시
          }
        }

        const responseStatus = response?.__httpStatus || 200;
        const successKey = responseStatus === 202
          ? 'postThreads.actions.success.threadScheduled'
          : 'postThreads.actions.success.threadScheduled';
        setSuccess(t(successKey));
      }

      // 🔥 게시 성공 후 처리 (이 함수는 예약 흐름에서만 호출되므로 isScheduled만 체크)
      if (isScheduled) {
        // 입력값 초기화
        setThreads([{ id: 1, text: "", isMain: true }]);
        setMediaFiles([]);
        setUploadProgress({});
        // 초기화 시에도 현재 시간 + 예약 최소 대기 시간으로 설정
        setScheduleDateTime(getMinScheduleDateTime());
        setCollectionConfig({ mode: "none", autoAddToCollection: false });
        setCurrentDraftId(null);
        
        // 예약게시인 경우 scheduled 탭으로 전환
        if (isScheduled) {
          setActiveTab('scheduled');
        }
        // 초안 편집 화면에서 예약으로 전환했다면 URL을 루트로 초기화하여 draft id 포커스 해제
        if (mode === 'edit-draft' && id) {
          skipNextNewModeResetRef.current = true;
          navigate('/post-thread', { replace: true });
        }
        // 최근 성공 지문 기록
        try {
          const fp = computeRequestFingerprint({ threadsList: fingerprintThreadsSnapshot, mediaList: fingerprintMediaSnapshot, when: scheduleDateTime, scheduled: isScheduled });
          const sm = readSuccessMap();
          sm[fp] = Date.now();
          writeSuccessMap(sm);
        } catch (_) {}
      }
    } catch (err) {
      // 오류 유형 구분
      if (err.response) {
        // 서버에서 응답이 왔지만 오류 상태 코드인 경우
        const statusCode = err.response.status;
        if (statusCode === 401 || statusCode === 403) {
          setError(t("postThreads.errors.unauthorizedOrExpired"));
        } else if (statusCode === 429) {
          setError(t("postThreads.errors.tooManyRequests"));
        } else if (statusCode >= 500) {
          setError(t("postThreads.errors.serverError"));
        } else {
          setError(
            t("postThreads.errors.requestProcessingError", { 
              statusCode, 
              message: err.response.data?.message || err.message
            })
          );
        }
      } else if (err.request) {
        // 요청은 성공했지만 응답을 받지 못한 경우
        setError(t("postThreads.errors.noServerResponse"));
      } else {
        // 요청 생성 자체가 실패한 경우
        setError(
          t("postThreads.errors.requestCreationFailed", { 
            message: err.message || t("postThreads.errors.threadPublishFailed")
          })
        );
      }

      // 오류 로깅
      console.error("API 호출 실패:", {
        errorType: err.name,
        message: err.message,
        stack: err.stack,
        isScheduled: isScheduled,
      });

      // 모호 실패 후 결과 확인 흐름(예약): 최근 생성된 태스크 유무 확인
      try {
        if (isScheduled && Array.isArray(firestoreTasks) && firestoreTasks.length > 0) {
          const legacy = convertThreadsToLegacyFormat(threads);
          const year = scheduleDateTime.getFullYear();
          const month = String(scheduleDateTime.getMonth() + 1).padStart(2, '0');
          const day = String(scheduleDateTime.getDate()).padStart(2, '0');
          const hours = String(scheduleDateTime.getHours()).padStart(2, '0');
          const minutes = String(scheduleDateTime.getMinutes()).padStart(2, '0');
          const seconds = '00';
          const executeAtStr = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

          const matched = firestoreTasks.find((t) => (t.executeAt === executeAtStr) && (t.text || '').trim() === (legacy.text || '').trim());
          if (matched) {
            setSuccess(t('postThreads.actions.success.scheduleAlreadyCreated'));
            // 입력값 초기화 (성공 플로우와 동일 처리)
            setThreads([{ id: 1, text: '', isMain: true }]);
            setMediaFiles([]);
            setUploadProgress({});
            setScheduleDateTime(getMinScheduleDateTime());
            setCollectionConfig({ mode: 'none', autoAddToCollection: false });
            setCurrentDraftId(null);
            // 🔥 모호 실패 후 성공 확인에서도 URL 초기화 제거, 예약 탭 유지
            setActiveTab('scheduled');
          }
        }
      } catch (checkErr) {
        console.warn('결과 확인 중 오류:', checkErr);
      }
    } finally {
      setLoading(false);
      setLoadingStep("");
      inFlightRef.current = false;
      // in-flight 지문 제거
      try {
        const fp = computeRequestFingerprint({ threadsList: threads, mediaList: mediaFiles, when: scheduleDateTime, scheduled: isScheduled });
        const m = readInflightMap();
        delete m[fp];
        writeInflightMap(m);
      } catch (_) {}
    }
  };




  // 🔥 공통 데이터 준비 함수
  const preparePostData = async (uploadPath = null, skipValidation = false) => {
    const { mainText, replies: structuredReplies } = convertThreadsToRichFormat(threads);
    const normalizedMainText = normalizeTextValue(mainText || '');
    const trimmedMainText = normalizedMainText.trim();

    if (!skipValidation && !trimmedMainText) {
      throw new Error(t("postThreads.errors.contentRequired"));
    }

    const defaultUploadBase = `threads/${localStorage.getItem("userID") || userID || 'anonymous'}/temp`;

    const processThreadMediaEntries = async (threadId, sourceMediaList, baseUploadPath) => {
      const wrappers = Array.isArray(sourceMediaList) ? sourceMediaList : [];

      if (wrappers.length === 0) {
        return {
          processedMediaFiles: [],
          apiMedia: [],
          firestoreMediaFiles: []
        };
      }

      if (!skipValidation && wrappers.length > 8) {
        throw new Error(t('postThreads.errors.maxFileLimit'));
      }

      const filesToUpload = [];
      const alreadyUploaded = [];
      const actualFiles = [];

      wrappers.forEach((wrapper, index) => {
        if (!wrapper) return;

        const inferredName = wrapper.name || wrapper.fileName || `media-${index + 1}`;
        const inferredType = wrapper.type || wrapper.fileType || '';
        const inferredSize = wrapper.size || wrapper.fileSize || 0;

        if (wrapper.file instanceof File) {
          filesToUpload.push(wrapper.file);
          actualFiles.push(wrapper.file);
        } else if (wrapper.url) {
          const preserved = {
            ...wrapper,
            id: wrapper.id || wrapper.storagePath || wrapper.url || `existing-${threadId}-${index}`,
            url: wrapper.url,
            preview: wrapper.preview || wrapper.url,
            name: inferredName,
            fileName: wrapper.fileName || inferredName,
            type: inferredType,
            fileType: inferredType,
            size: inferredSize,
            fileSize: inferredSize,
            storagePath: wrapper.storagePath,
            isTemporary: false,
            threadId
          };
          delete preserved.file;
          delete preserved.needsReupload;
          alreadyUploaded.push(preserved);
        }
      });

      if (!skipValidation) {
        const MAX_VIDEO_SIZE = 300 * 1024 * 1024;
        for (const file of actualFiles) {
          if (file.type && file.type.startsWith('video/') && file.size > MAX_VIDEO_SIZE) {
            throw new Error(t('postThreads.errors.videoFileTooLarge', { fileName: file.name, currentSize: Math.round(file.size / 1024 / 1024) }));
          }
        }
      }

      const finalUploadPath = baseUploadPath || `${defaultUploadBase}/${threadId}`;

      let processedMediaFiles = [...alreadyUploaded];

      if (filesToUpload.length > 0) {
        setLoadingStep(t('postThreads.actions.loading.uploading'));
        const uploadedMedia = await uploadMultipleMedia(filesToUpload, finalUploadPath);
        const uploadedMetadata = uploadedMedia.map((media, index) => {
          const normalizedType = media.fileType || media.type || '';
          const normalizedSize = typeof media.fileSize === 'number' ? media.fileSize : (typeof media.size === 'number' ? media.size : 0);

          return {
            url: media.url,
            fileName: media.fileName,
            fileType: normalizedType,
            fileSize: normalizedSize,
            storagePath: media.path,
            id: `uploaded-${threadId}-${Date.now()}-${index}`,
            name: media.fileName,
            type: normalizedType,
            size: normalizedSize,
            preview: media.url,
            isTemporary: false,
            threadId
          };
        });
        processedMediaFiles = [...alreadyUploaded, ...uploadedMetadata];
      }

      const apiMedia = processedMediaFiles.map((media) => {
        const payload = {
          url: media.url,
          fileName: media.fileName || media.name || 'unknown',
          fileType: media.fileType || media.type || null,
          fileSize: typeof media.fileSize === 'number' ? media.fileSize : (typeof media.size === 'number' ? media.size : null),
          storagePath: media.storagePath || null
        };

        Object.keys(payload).forEach((key) => {
          if (payload[key] === undefined || payload[key] === null) {
            delete payload[key];
          }
        });

        return payload;
      });

      const firestoreMediaFiles = processedMediaFiles.map((media) => {
        const firestoreData = {
          id: media.id || `media-${threadId}-${Date.now()}-${Math.random()}`,
          name: media.name || media.fileName || 'unknown',
          type: media.type || media.fileType || 'application/octet-stream',
          size: media.size || media.fileSize || 0,
          url: media.url || '',
          preview: media.preview || media.url || '',
          isTemporary: false,
          threadId
        };
        if (media.storagePath) {
          firestoreData.storagePath = media.storagePath;
        } else if (media.path) {
          firestoreData.storagePath = media.path;
        }
        return firestoreData;
      }).filter((media) => media.url && media.url.length > 0);

      return {
        processedMediaFiles,
        apiMedia,
        firestoreMediaFiles
      };
    };

    const mainMediaList = mediaByThread.get(mainThreadId) || [];
    const mainUploadPath = uploadPath ? `${uploadPath}/main` : null;
    const mainMediaResult = await processThreadMediaEntries(mainThreadId, mainMediaList, mainUploadPath);

    const repliesPayload = [];
    const repliesProcessedMap = {};
    const repliesFirestoreMap = {};
    const structuredRepliesWithMedia = [];

    for (let index = 0; index < structuredReplies.length; index += 1) {
      const reply = structuredReplies[index];
      const normalizedReplyText = normalizeTextValue(reply.text || '');
      const replyTrimmed = normalizedReplyText.trim();
      const replyMediaList = mediaByThread.get(reply.id) || [];
      const replyUploadPath = uploadPath ? `${uploadPath}/reply-${reply.id}` : null;
      const replyMediaResult = await processThreadMediaEntries(reply.id, replyMediaList, replyUploadPath);

      const hasMedia = replyMediaResult.apiMedia.length > 0;

      if (!skipValidation && !hasMedia && !replyTrimmed) {
        throw new Error(t('postThreads.errors.emptyReplyGeneral', { index: index + 1 }));
      }

      repliesProcessedMap[reply.id] = replyMediaResult.processedMediaFiles;
      repliesFirestoreMap[reply.id] = replyMediaResult.firestoreMediaFiles;

      if (replyTrimmed.length === 0 && !hasMedia) {
        continue;
      }

      repliesPayload.push({
        text: replyTrimmed,
        media: replyMediaResult.apiMedia
      });

      structuredRepliesWithMedia.push({
        id: reply.id,
        text: replyTrimmed,
        media: replyMediaResult.apiMedia
      });
    }

    const combinedProcessedMedia = [
      ...mainMediaResult.processedMediaFiles,
      ...Object.values(repliesProcessedMap).flat()
    ];

    return {
      text: trimmedMainText,
      repliesForApi: repliesPayload,
      mainMediaForApi: mainMediaResult.apiMedia,
      mainMediaFirestore: mainMediaResult.firestoreMediaFiles,
      combinedProcessedMedia,
      replyMediaFirestore: repliesFirestoreMap,
      structuredReplies: structuredRepliesWithMedia
    };
  };

  // 임시저장 핸들러
  const handleSaveDraft = async () => {
    try {
      setLoading(true);
      setLoadingStep(t("postThreads.actions.loading.savingDraft"));
      
      const {
        mainMediaFirestore,
        combinedProcessedMedia,
        replyMediaFirestore
      } = await preparePostData(`threads/${userID}/drafts`);
      
      setLoadingStep(t("postThreads.actions.loading.savingToStorage"));
      
      // Firestore용 초안 데이터 (undefined 값 제거)
      const cleanDraftData = {
        threads,
        mediaFiles: mainMediaFirestore || [],
        replyMediaFiles: replyMediaFirestore || {},
        collectionConfig,
        scheduleDateTime: scheduleDateTime
      };
      Object.keys(cleanDraftData).forEach(key => {
        if (cleanDraftData[key] === undefined) {
          delete cleanDraftData[key];
        }
      });

      // ✅ Optimistic 드래프트 카드 추가
      const tempDraftId = `temp-draft-${Date.now()}`;
      const main = threads.find(t => t.isMain);
      const optimisticDraft = {
        id: tempDraftId,
        isTemporary: true,
        title: (main?.text || '').substring(0, 60) || t('postThreads.draft.defaultContent'),
        threads,
        mediaFiles: combinedProcessedMedia || [],
        updatedAt: Date.now()
      };
      setOptimisticDrafts(prev => [optimisticDraft, ...prev]);
      
      console.log('[handleSaveDraft] 정제된 초안 데이터:', cleanDraftData);
      
      const result = await saveDraft(userID, cleanDraftData, currentDraftId);
      
      // 저장 후 상태 업데이트 (미디어가 비어도 UI와 동기화)
      setMediaFiles(combinedProcessedMedia);
      
      if (result.action === 'created') {
        setCurrentDraftId(result.draftId);
        setSuccess(t('postThreads.actions.success.draftSaved'));
        // 새 초안 생성 후 동일 초안으로 이어쓰기 위해 draft 라우트로 이동
        navigate(`/post-thread/draft/${result.draftId}`, { replace: true });
      } else {
        setSuccess(t('postThreads.actions.success.draftUpdatedSuccess'));
      }

      // 낙관적 드래프트 제거 (실데이터는 loadDrafts로 보강)
      setOptimisticDrafts(prev => prev.filter(d => d.id !== tempDraftId));
      setDraftUpdateTrigger(prev => prev + 1);
    } catch (error) {
      setError(error.message);
      // 실패 시 낙관적 드래프트 제거
      setOptimisticDrafts(prev => prev.filter(d => d.isTemporary));
    } finally {
      setLoading(false);
      setLoadingStep("");
    }
  };

  // 중복 정의 제거됨 (상단 useCallback 버전 사용)


  // 초안 삭제
  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm(t('postThreads.confirm.deleteDraft'))) return;
    
    try {
      await deleteDraft(userID, draftId);
      setDrafts(prev => prev.filter(d => d.id !== draftId));
      setSuccess(t('postThreads.actions.success.draftDeleted'));
      
      // 🔥 임시저장 삭제 완료 트리거 (drafts 리스트 업데이트)
      setDraftUpdateTrigger(prev => prev + 1);
    } catch (error) {
      setError(error.message);
    }
  };

  // 예약글 삭제 (ScheduledPostsList에서 호출)
  const handleDeleteScheduledPost = async (postId) => {
    if (!window.confirm(t('postThreads.confirm.deleteScheduledPost'))) return;
    
    try {
      setLoading(true);
      await deleteFirestoreTask(postId);
      setSuccess(t('postThreads.actions.success.scheduledPostDeleted'));
    } catch (error) {
      setError(t('postThreads.errors.scheduledPostDeleteFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 수정 모드에서 기존 임시저장글 업데이트
  const handleUpdateDraft = async () => {
    if (!isEditMode) return;
    // edit-draft 모드에서 변경 사항이 없으면 조기 종료 (중복 API 호출 방지)
    if (mode === 'edit-draft' && !isDraftDirty) return;
    
    try {
      setLoading(true);
      setLoadingStep(t("postThreads.actions.loading.updating"));
      
      const {
        mainMediaFirestore,
        combinedProcessedMedia,
        replyMediaFirestore
      } = await preparePostData(`threads/${userID}/drafts`);
      
      setLoadingStep(t("postThreads.actions.loading.saving"));
      
      // Firestore용 초안 데이터 (undefined 값 제거)
      const cleanDraftData = {
        threads,
        mediaFiles: mainMediaFirestore || [],
        replyMediaFiles: replyMediaFirestore || {},
        collectionConfig,
        scheduleDateTime: scheduleDateTime
      };
      
      // undefined 값 제거
      Object.keys(cleanDraftData).forEach(key => {
        if (cleanDraftData[key] === undefined) {
          delete cleanDraftData[key];
        }
      });
      
      // 저장 후 상태 업데이트 (미디어 삭제도 반영)
      setMediaFiles(combinedProcessedMedia);
      
      if (mode === 'edit-draft') {
        // 기존 임시저장글 업데이트
        await saveDraft(userID, cleanDraftData, id);
        setSuccess(t('postThreads.actions.success.draftUpdated'));

        // 저장 성공 시 현재 상태를 기준으로 스냅샷 갱신 → 버튼 비활성화
        setBaselineDraftSnapshot(getCurrentStateSnapshot());
      } else if (mode === 'edit-scheduled') {
        // 예약글을 임시저장으로 변환
        await saveDraft(userID, cleanDraftData);
        setSuccess(t('postThreads.actions.success.draftSavedAsTemp'));
      }

      // 🔥 임시저장 완료 트리거 (리스트는 업데이트하되, 에디터 상태와 현재 화면은 유지)
      setDraftUpdateTrigger(prev => prev + 1);
      // 에디터 데이터 및 현재 탭은 유지
    } catch (error) {
      setError(error.message || t('postThreads.errors.draftSaveFailed'));
    } finally {
      setLoading(false);
      setLoadingStep("");
    }
  };

  return (
    <>
      {/* Focus 모드일 때 전체화면 렌더링 */}
      {isFocusMode && (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            minHeight: "calc(100vh - 64px)",
            backgroundColor: "background.default",
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <FullScreenEditor
            threads={threads}
            onThreadsChange={handleThreadsChange}
            mediaByThread={mediaByThread}
            onThreadMediaChange={handleThreadMediaChange}
            loading={loading}
            loadingStep={loadingStep}
            isScheduled={isScheduled}
            setIsScheduled={setIsScheduled}
            scheduleDateTime={scheduleDateTime}
            setScheduleDateTime={setScheduleDateTime}
            collectionConfig={collectionConfig}
            setCollectionConfig={setCollectionConfig}
            myCollections={myCollections}
            handlePost={handlePost}
            handleThreadsAPIPost={handleThreadsAPIPost}
            isPostDisabled={isPostDisabled}
            hasOverLimitText={hasOverLimitText}
            MAX_TEXT_LENGTH={MAX_TEXT_LENGTH}
            onSaveDraft={handleSaveDraft}
            hasNoContent={hasNoContent}
            isEditMode={isEditMode}
            onDelete={handleDelete}
            onUpdateDraft={handleUpdateDraft}
            isDraftDirty={isDraftDirty}
            isEditDraft={mode === 'edit-draft'}
          />

        </Box>
      )}
        
      {/* Preview 모드 - PageContainer로 통합 관리 - 항상 렌더링 */}
      <Box sx={{ display: isFocusMode ? 'none' : 'block' }}>
        <PageContainer expand={true}>
          <Box
            sx={{
              // 모바일에서 ThreadComposer만 화면 전체 기준 중앙 정렬
              width: {
                xs: "100vw", // 모바일: 전체 화면 너비
                xl: "100%", // PC: PageContainer 너비
              },
              maxWidth: "384px",
              margin: "0 auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: "384px",
                marginLeft: {
                  xs: 0,
                  sm: 0,
                },
              }}
            >
              <ThreadComposer
                threads={threads}
                onThreadsChange={handleThreadsChange}
                mediaByThread={mediaByThread}
                onThreadMediaChange={handleThreadMediaChange}
                loading={loading}
                maxThreads={5}
                mediaUploadVariant="inline"
              />
              <Box sx={{ mt: 3, width: "100%" }}>
                <ScheduleSection
                  isScheduled={isScheduled}
                  setIsScheduled={setIsScheduled}
                  scheduleDateTime={scheduleDateTime}
                  setScheduleDateTime={setScheduleDateTime}
                  loading={loading}
                  hideToggle={false}
                />
              </Box>

              {/* 컬렉션 선택: 항상 표시하되 예약이 아닐 때 비활성화 (포커스/프리뷰 동기화) */}
              <Box sx={{ mt: 3, width: "100%" }}>
                <CollectionSelector
                  value={collectionConfig}
                  onChange={setCollectionConfig}
                  collections={myCollections}
                  loading={loading}
                  disabled={loading || !isScheduled}
                />
              </Box>

              <Box sx={{ mt: 2, width: "100%" }}>
                <Stack direction="column" spacing={2} sx={{ width: "100%" }}>
                  <ButtonBasic
                    onClick={isScheduled ? handlePost : handleThreadsAPIPost}
                    loading={loading}
                    disabled={isActionDisabled}
                    size="xl"
                    widthType="full"
                    actionType="primary"
                    radius="full"
                  >
                    {getButtonText()}
                  </ButtonBasic>
                  
                  
                  {mode !== 'edit-scheduled' && (
                    <ButtonBasic
                      onClick={isEditMode ? handleUpdateDraft : handleSaveDraft}
                      variant="outlined"
                      disabled={hasNoDraftContent || loading || (isEditMode && mode === 'edit-draft' && !isDraftDirty)}
                      size="xl"
                      widthType="full"
                      radius="full"
                    >
                      {t("postThreads.buttons.saveDraft")}
                    </ButtonBasic>
                  )}
                  
                  {/* 하단 툴바 - 수정 모드에서만 표시 */}
                  {isEditMode && (
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 1, 
                      mt: 2,
                      pt: 2,
                      borderTop: 1,
                      borderColor: 'divider'
                    }}>
                      <ButtonBasic
                        onClick={handleDelete}
                        variant="outlined"
                        color="error"
                        disabled={loading}
                        size="lg"
                        widthType="auto"
                        radius="full"
                        sx={{ flex: 1 }}
                      >
                        {t("postThreads.buttons.deletePost")}
                      </ButtonBasic>
                      <ButtonBasic
                        onClick={() => navigate('/post-thread')}
                        variant="outlined"
                        disabled={loading}
                        size="lg"
                        widthType="auto"
                        radius="full"
                        sx={{ flex: 1 }}
                      >
                        {t("postThreads.buttons.newPost")}
                      </ButtonBasic>
                    </Box>
                  )}
                </Stack>

                {hasOverLimitText && (
                  <Box
                    sx={{
                      fontSize: "12px",
                      color: "error.main",
                      mt: 1,
                      textAlign: "center",
                    }}
                  >
                    {t("postThreads.validation.textLengthExceeded", { maxLength: MAX_TEXT_LENGTH })}
                  </Box>
                )}

              </Box>
            </Box>
          </Box>
        </PageContainer>
      </Box>

      {/* 라우터 하단: 예약/초안 리스트 (모드와 무관하게 단일 배치, fixed 금지) */}
      <Box
        sx={{
          width: "100%",
          mt: 2,
          p: 2,
          borderTop: 1,
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {t('postThreads.savedItemsTitle', { defaultValue: '저장 게시물' })}
          </Typography>
        </Box>
        <ScheduledPostsList
          scheduledPosts={scheduledForUI}
          loadingPosts={firestoreLoading}
          openEditModal={() => {}}
          drafts={draftsForUI}
          loadingDrafts={false}
          onLoadDraft={() => {}}
          onDeleteDraft={handleDeleteDraft}
          onDeleteScheduledPost={handleDeleteScheduledPost}
          activeTab={activeTab}
          onTabChange={(value) => {
            console.log('[PostThreads] Tab changed to:', value);
            setActiveTab(value);
          }}
        />
      </Box>

      {/* 모든 모드에서 공통으로 사용하는 Floating Notification */}
      <NotificationSection
        error={error}
        success={success}
        setError={setError}
        setSuccess={setSuccess}
      />

      {/* 실행중(executing) 태스크가 존재하면 고정 로딩 표시 */}
      <DataLoader
        msg={t('postThreads.actions.loading.publishing')}
        isLoad={hasExecutingTasks}
        showElapsedTime={true}
        isLogin={!!userID}
        isPublicRoute={false}
        useProgressiveMode={false}
      />
    </>
  );
};

export default PostThreads;
